import { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../utils/calculations';
import styles from './AddMoneyModal.module.css';

const AddMoneyModal = ({ goal, onAdd, onClose }) => {
  const [amount, setAmount] = useState('');
  const [closing, setClosing] = useState(false);
  const inputRef = useRef(null);

  const parsed = parseFloat(amount) || 0;
  const newTotal = goal.currentSaved + parsed;
  const canAdd = parsed > 0 && newTotal <= goal.amount;
  const overMax = parsed > 0 && newTotal > goal.amount;

  useEffect(() => {
    // Focus input after mount animation
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, []);

  // Close Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleClose = () => {
    setClosing(true);
  };

  const handleAnimationEnd = () => {
    if (closing) onClose();
  };

  const handleAdd = () => {
    if (!canAdd) return;
    setClosing(true);
    // Pass amount up; parent fires toast after modal unmounts
    onAdd(parsed);
  };

  return (
    <div
      className={`${styles.backdrop} ${closing ? styles.backdropOut : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      onAnimationEnd={handleAnimationEnd}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className={`${styles.sheet} ${closing ? styles.sheetOut : ''}`}>
        <div className={styles.handle} />

        <h2 id="modal-title" className={styles.title}>Add to {goal.name}</h2>

        <div className={styles.progress}>
          <span className={styles.saved}>{formatCurrency(goal.currentSaved)}</span>
          <span className={styles.separator}> of </span>
          <span className={styles.target}>{formatCurrency(goal.amount)}</span>
        </div>

        <div className={styles.inputWrap}>
          <span className={styles.prefix}>$</span>
          <input
            ref={inputRef}
            type="number"
            min="1"
            className={styles.input}
            placeholder="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            aria-label="Amount to add"
          />
        </div>

        {parsed > 0 && (
          <p className={`${styles.helper} ${overMax ? styles.helperError : ''}`}>
            {overMax
              ? `Max you can add: ${formatCurrency(goal.amount - goal.currentSaved)}`
              : `New total: ${formatCurrency(newTotal)}`
            }
          </p>
        )}

        <div className={styles.actions}>
          <button
            className={`${styles.addBtn} ${!canAdd ? styles.addBtnDisabled : ''}`}
            onClick={handleAdd}
            disabled={!canAdd}
          >
            Add {parsed > 0 && !overMax ? formatCurrency(parsed) : ''}
          </button>
          <button className={styles.cancelBtn} onClick={handleClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMoneyModal;
